import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';
import { debounce } from 'lodash';
import { Helmet } from 'react-helmet-async';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import Button from '@mui/material/Button';
import Latest from '../components/Latest';
import HubList from '../components/HubList';
import { useGetCulturalPosts } from '../../../hooks/hubHooks';
import ErrorDisplay from '../../../components/common/ErrorDisplay';
import { AppConfig } from '../../../core';
import Chip from '@mui/material/Chip';
import RecruitBanner from '../components/RecruitBanner';
import { sanitiseInput } from '../../../utils/sanitiseUserInput';
import { CulturalPost } from '../../../types';

type SearchProps = {
  value: string;
  onSearch: (e: any) => void;
  placeholder: string;
};

const allCategories = [
  'Marriage',
  'Food',
  'Fashion',
  'Stories',
  'Tradition',
  'Art',
  'Music',
] as const;

export function Search({ value, onSearch, placeholder }: SearchProps) {
  return (
    <FormControl
      sx={{ width: '100%', maxWidth: { md: '40rem' } }}
      variant="outlined"
    >
      <OutlinedInput
        size="small"
        id="search"
        placeholder={placeholder}
        sx={{
          flexGrow: 1,
          borderRadius: 2,
          bgcolor: 'background.paper',
          '& .MuiOutlinedInput-input': {
            py: 1.25,
            fontSize: '0.95rem',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'divider',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'text.disabled',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 1.5,
            borderColor: 'primary.main',
          },
        }}
        value={value}
        inputProps={{
          'aria-label': 'search',
        }}
        onChange={onSearch}
      />
    </FormControl>
  );
}

export function CulturalPage() {
  const { palette, typography } = useTheme();
  const location = useLocation();
  const [tempSearch, setTempSearch] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [feedPosts, setFeedPosts] = useState<CulturalPost[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    'All categories',
  );
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const isFetchingNextRef = useRef(false);
  const categoryQuery =
    selectedCategory === 'All categories' ? '' : (selectedCategory ?? '');
  const effectiveQuery = debouncedSearchQuery || categoryQuery;

  const debouncedSearch = useMemo(
    () =>
      debounce((query: string) => {
        setDebouncedSearchQuery(query);
      }, 300),
    [],
  );

  useEffect(() => {
    const initialQuery =
      typeof location.state === 'object' &&
      location.state !== null &&
      'query' in location.state &&
      typeof (location.state as { query?: unknown }).query === 'string'
        ? (location.state as { query: string }).query
        : '';

    if (initialQuery) {
      const cleanQuery = sanitiseInput(initialQuery);
      setTempSearch(cleanQuery);
      setDebouncedSearchQuery(cleanQuery);
    }
  }, [location.state]);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    debouncedSearch(tempSearch);
  }, [tempSearch, debouncedSearch]);

  useEffect(() => {
    setFeedPosts([]);
    setHasMore(true);
    isFetchingNextRef.current = false;
    setPage(1);
  }, [effectiveQuery]);

  const { data, isLoading, isError, error, refetch } = useGetCulturalPosts(
    effectiveQuery,
    page,
    limit,
  );

  const isInitialLoading = isLoading && page === 1 && feedPosts.length === 0;
  const isLoadingMore = isLoading && page > 1;

  const loadNextPage = useCallback(() => {
    if (isFetchingNextRef.current || isLoading || isError || !hasMore) {
      return;
    }

    isFetchingNextRef.current = true;
    setPage((previousPage) => previousPage + 1);
  }, [hasMore, isLoading, isError]);

  const handleClick = (value: string) => {
    setTempSearch('');
    if (value === selectedCategory) {
      return;
    }

    setSelectedCategory(value);
  };

  useEffect(() => {
    if (!data) return;

    const postsResponse = data as any;
    const incomingPosts: CulturalPost[] =
      postsResponse?.cultural_contexts || [];

    setFeedPosts((previousPosts) => {
      if (page === 1) {
        return incomingPosts;
      }

      const existingIds = new Set(previousPosts.map((post) => post.id));
      const newUniquePosts = incomingPosts.filter(
        (post) => !existingIds.has(post.id),
      );

      return [...previousPosts, ...newUniquePosts];
    });

    const currentPage = Number(postsResponse?.current_page ?? page);
    const totalPages = Number(postsResponse?.total_pages ?? currentPage);
    const hasNextPage = currentPage < totalPages && incomingPosts.length > 0;
    setHasMore(hasNextPage);
    isFetchingNextRef.current = false;
  }, [data, page]);

  useEffect(() => {
    if (!loadMoreRef.current || !hasMore || isLoading || isError) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry.isIntersecting) {
          return;
        }

        loadNextPage();
      },
      { rootMargin: '240px 0px' },
    );

    observer.observe(loadMoreRef.current);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, isLoading, isError, loadNextPage]);

  useEffect(() => {
    if (!hasMore || isLoading || isError) {
      return;
    }

    const onScroll = () => {
      if (!loadMoreRef.current) return;

      const { top } = loadMoreRef.current.getBoundingClientRect();
      if (top <= window.innerHeight + 240) {
        loadNextPage();
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [hasMore, isLoading, isError, loadNextPage, feedPosts.length]);

  if (isError) {
    return <ErrorDisplay error={error} onRetry={refetch} />;
  }

  const latestPosts = feedPosts.slice(0, 4);
  const remainingPosts = feedPosts.slice(4);
  return (
    <Box mt="64px" sx={{ height: '100%' }}>
      <Helmet>
        <title>Cultural Hub | {AppConfig.appName}</title>
        <meta
          name="description"
          content="View cultural posts about African and Caribbean heritage"
        />
        <meta
          property="og:title"
          content={`${AppConfig.appName} - Cultural Hub`}
        />
        <meta
          property="og:description"
          content="View cultural posts about African and Caribbean heritage"
        />
      </Helmet>
      <RecruitBanner />
      <Container
        maxWidth="xl"
        component="main"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          my: 14,
          gap: 4,
          px: { xs: 3, sm: 8 },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Box display="flex" flexDirection={'column'} gap={2}>
            <Typography
              sx={{
                fontSize: {
                  xs: typography.h3.fontSize,
                  md: typography.h1.fontSize,
                },
                fontWeight: {
                  xs: typography.h3.fontWeight,
                  md: typography.h1.fontWeight,
                },
                color: palette.text.primary,
                fontFamily:
                  '"Playfair Display", "Georgia", "Times New Roman", serif !important',
              }}
              gutterBottom
            >
              The Library of Us.
            </Typography>
            <Typography
              sx={{
                color: palette.text.primary,
                fontSize: {
                  xs: typography.body1.fontSize,
                  md: '20px',
                },
                lineHeight: '1.8',
                mb: 1,
              }}
            >
              When a grandmother passes, a library of recipes can disappear.
              When elders stop speaking a dialect, generations of meaning can
              fade. When traditions are not practiced, memory loses its home.
              <br />
              <Box
                component="span"
                sx={{ fontWeight: 700, color: palette.text.primary }}
              >
                Cultural Hub exists to keep that memory alive.
              </Box>
              <br />
              We are capturing African and Caribbean living traditions, from
              Senegalese naming ceremonies to Ethiopian coffee rituals, from
              Igbo proverbs to Swahili lullabies. Keeping our heritage alive
              through context, voice, and story.
              <br />
              <br />
              <Box
                component="span"
                sx={{ fontWeight: 600, color: palette.text.primary }}
              >
                Discover. Learn. Contribute. Preserve what matters.
              </Box>
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: { xs: 'flex', sm: 'none' },
            width: '100%',
          }}
        >
          <Search
            value={tempSearch}
            onSearch={(e) => setTempSearch(sanitiseInput(e.target.value))}
            placeholder="Search the library"
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column-reverse', md: 'row' },
            width: '100%',
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', md: 'center' },
            gap: 2,
            p: { xs: 1.5, md: 1 },
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            backgroundColor: 'rgba(255,255,255,0.4)',
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              flexDirection: 'row',
              gap: 1,
              overflow: 'auto',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Chip
              onClick={() => handleClick('All categories')}
              size="medium"
              label="All categories"
              color={
                selectedCategory === 'All categories' ? 'primary' : 'default'
              }
              variant={
                selectedCategory === 'All categories' ? 'filled' : 'outlined'
              }
              sx={{
                cursor: 'pointer',
                borderRadius: 999,
                fontWeight: 600,
                px: 0.5,
              }}
            />
            {allCategories.map((category) => {
              return (
                <Chip
                  key={category}
                  onClick={() => handleClick(category)}
                  size="medium"
                  label={category}
                  color={selectedCategory === category ? 'primary' : 'default'}
                  variant={
                    selectedCategory === category ? 'filled' : 'outlined'
                  }
                  sx={{
                    cursor: 'pointer',
                    borderRadius: 999,
                    fontWeight: 600,
                    px: 0.5,
                  }}
                />
              );
            })}
          </Box>
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              flexDirection: 'row',
              width: { xs: '100%', md: '42%' },
              justifyContent: { xs: 'stretch', md: 'flex-end' },
            }}
          >
            <Search
              value={tempSearch}
              onSearch={(e) => setTempSearch(sanitiseInput(e.target.value))}
              placeholder="Search the library"
            />
          </Box>
        </Box>
        {!isInitialLoading ? (
          <>
            {latestPosts.length > 0 && (
              <Latest posts={latestPosts} isNextPage={false} />
            )}
            {remainingPosts.length > 0 && <HubList posts={remainingPosts} />}

            {hasMore && feedPosts.length > 0 && (
              <Box
                ref={loadMoreRef}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  pt: 3,
                  gap: 1,
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {isLoadingMore ? (
                  <CircularProgress size={24} />
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Scroll to load more records
                    </Typography>
                    <Button size="small" variant="text" onClick={loadNextPage}>
                      Load more now
                    </Button>
                  </>
                )}
              </Box>
            )}

            {!isLoading && feedPosts.length === 0 && (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 5,
                  px: 3,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  background:
                    'linear-gradient(120deg, rgba(232, 223, 205, 0.25), rgba(232, 234, 219, 0.25))',
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  No record found {effectiveQuery && `for ${effectiveQuery}`}.
                </Typography>
              </Box>
            )}
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100dvh',
              mt: '64px',
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Container>
    </Box>
  );
}
